import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ImpersonationTarget } from '../../types/models/ImpersonationTarget';

interface ImpersonationContextType {
  target: ImpersonationTarget | null;
  isImpersonating: boolean;
  startImpersonation: (target: ImpersonationTarget) => void;
  stopImpersonation: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

const SESSION_STORAGE_KEY = 'ancalagon.impersonation.target';

/**
 * Reads the persisted impersonation target from sessionStorage.
 * Returns null on missing, malformed, or unparseable entries.
 * Defensive against corrupted storage — never throws.
 */
function readPersistedTarget(): ImpersonationTarget | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;

    // Minimal shape validation — we trust ourselves to write valid data,
    // but defend against tampering or corruption.
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'id' in parsed &&
      typeof (parsed as ImpersonationTarget).id === 'number' &&
      'name' in parsed &&
      typeof (parsed as ImpersonationTarget).name === 'string'
    ) {
      return parsed as ImpersonationTarget;
    }
    // Malformed — clear it so we don't keep tripping over it.
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    return null;
  } catch {
    // sessionStorage can throw in private-mode / quota-exceeded scenarios.
    return null;
  }
}

export const ImpersonationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialise from sessionStorage on first render. Lazy initialiser
  // ensures the read happens once, not on every render.
  const [target, setTarget] = useState<ImpersonationTarget | null>(() => readPersistedTarget());

  // Persist on every change. Keeping this in a useEffect (rather than
  // inside the action callbacks) means a future direct-setState path
  // would still persist correctly.
  useEffect(() => {
    try {
      if (target === null) {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      } else {
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(target));
      }
    } catch {
      // sessionStorage write failures are non-fatal — impersonation still
      // works for the current session, it just won't survive a refresh.
    }
  }, [target]);

  const startImpersonation = useCallback((newTarget: ImpersonationTarget) => {
    setTarget(newTarget);
  }, []);

  const stopImpersonation = useCallback(() => {
    setTarget(null);
  }, []);

  const value = useMemo<ImpersonationContextType>(
    () => ({
      target,
      isImpersonating: target !== null,
      startImpersonation,
      stopImpersonation,
    }),
    [target, startImpersonation, stopImpersonation],
  );

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
};

/**
 * Read the current impersonation state. Use this for display logic
 * (banners, conditional rendering, etc.).
 */
export function useImpersonation(): {
  target: ImpersonationTarget | null;
  isImpersonating: boolean;
} {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return { target: context.target, isImpersonating: context.isImpersonating };
}

/**
 * Get callbacks to start/stop impersonation. Separating actions from state
 * means components that only trigger impersonation (buttons) won't re-render
 * when the state changes.
 */
export function useImpersonationActions(): {
  startImpersonation: (target: ImpersonationTarget) => void;
  stopImpersonation: () => void;
} {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonationActions must be used within an ImpersonationProvider');
  }
  return {
    startImpersonation: context.startImpersonation,
    stopImpersonation: context.stopImpersonation,
  };
}
